// SD51 / FR-M3c / FR-AD7: the notetaker bot's per-platform kill switches, on /ops/notetaker.
// GET /ops/notetaker, PUT/DELETE /ops/notetaker/platforms/{platform}. A switch saves on change and
// applies on the next join in every process; "Use environment default" drops the console value
// so GRACKLE_BOT_*_ENABLED applies again. An org's own bot switch is on its ops org page.
import { useEffect, useState } from "react";
import { Badge, Card, ToggleRow } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { BotPlatform, OpsBotPlatformOut, OpsLeaveOut, OpsLeaveSettings, OpsNotetakerOut } from "../api/types";
import { Button, Field, Notice } from "../components/ui";

const PLATFORM_LABEL: Record<BotPlatform, string> = {
  meet: "Google Meet",
  zoom: "Zoom",
  teams: "Microsoft Teams",
};

const ORDER: BotPlatform[] = ["meet", "zoom", "teams"];

function PlatformRow({
  item,
  busy,
  onSet,
  onReset,
}: {
  item: OpsBotPlatformOut;
  busy: boolean;
  onSet: (enabled: boolean) => void;
  onReset: () => void;
}) {
  const label = PLATFORM_LABEL[item.platform];
  const description =
    item.source === "console"
      ? `Set here${item.updated_by_email ? ` by ${item.updated_by_email}` : ""}${
          item.updated_at ? ` on ${new Date(item.updated_at).toLocaleString()}` : ""
        }. The environment default is ${item.env_enabled ? "on" : "off"}.`
      : `Environment default (${item.env_enabled ? "on" : "off"}). Changing it here overrides the environment.`;
  return (
    <div data-testid={`bot-platform-${item.platform}`}>
      <ToggleRow
        divided
        label={
          <span className="cluster">
            {label}
            <Badge size="sm" tone={item.source === "console" ? "success" : "accent"}>
              {item.source === "console" ? "Console" : "Environment"}
            </Badge>
          </span>
        }
        description={description}
        checked={item.enabled}
        onChange={(v) => {
          if (!busy) onSet(v);
        }}
      />
      {item.source === "console" ? (
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onReset}>
          Use environment default
        </Button>
      ) : null}
    </div>
  );
}

const LEAVE_FIELDS: { key: keyof OpsLeaveSettings; label: string; help: string }[] = [
  {
    key: "waiting_room_timeout_secs",
    label: "Waiting room (s)",
    help: "Give up on admission after this long.",
  },
  {
    key: "noone_joined_timeout_secs",
    label: "Nobody joined (s)",
    help: "Leave if no one else ever shows up.",
  },
  {
    key: "everyone_left_timeout_secs",
    label: "Everyone left (s)",
    help: "Leave this long after the bot is alone.",
  },
  {
    key: "silence_timeout_secs",
    label: "Silence (s)",
    help: "Leave after this much continuous silence.",
  },
  {
    key: "silence_activate_after_secs",
    label: "Silence rule starts after (s)",
    help: "Silence only counts once the bot has been recording this long, so a quiet start doesn't end it.",
  },
];

/** SD56: the auto-leave rules, below the platform switches. Numbers are edited as text and
 * validated on save; the server enforces 1..=86400 (0 allowed only for arming) either way. */
function LeaveSection({
  leave,
  onSaved,
  onError,
}: {
  leave: OpsLeaveOut;
  onSaved: (next: OpsLeaveOut) => void;
  onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Record<keyof OpsLeaveSettings, string>>(() => toDraft(leave.settings));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(toDraft(leave.settings));
  }, [leave]);

  async function save() {
    const parsed: Partial<OpsLeaveSettings> = {};
    for (const field of LEAVE_FIELDS) {
      const n = Number.parseInt(draft[field.key], 10);
      if (!Number.isFinite(n) || n < 0 || n > 86_400) {
        onError(`${field.label} must be a number of seconds (0–86400).`);
        return;
      }
      parsed[field.key] = n;
    }
    setBusy(true);
    try {
      onSaved(await api<OpsLeaveOut>("PUT", "/ops/notetaker/leave", parsed));
    } catch (err) {
      onError(err instanceof ApiFail ? err.message : "Couldn't save the auto-leave rules.");
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    try {
      onSaved(await api<OpsLeaveOut>("DELETE", "/ops/notetaker/leave"));
    } catch (err) {
      onError(err instanceof ApiFail ? err.message : "Couldn't reset the auto-leave rules.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="bot-leave-settings">
      <div className="cluster ops-leave-head">
        <span className="gk-field__label">Auto-leave</span>
        <Badge size="sm" tone={leave.source === "console" ? "success" : "accent"}>
          {leave.source === "console" ? "Console" : "Defaults"}
        </Badge>
      </div>
      <p className="gk-sm ops-card-note">
        When the bot leaves on its own: admission that never comes, an empty meeting, everyone
        gone, or a long silence. Applies from the next join.
        {leave.source === "console" && leave.updated_by_email
          ? ` Set by ${leave.updated_by_email}${leave.updated_at ? ` on ${new Date(leave.updated_at).toLocaleString()}` : ""}.`
          : ""}
      </p>
      <div className="admin-two-col">
        {LEAVE_FIELDS.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            className="mono"
            inputMode="numeric"
            value={draft[field.key]}
            onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
            help={field.help}
          />
        ))}
      </div>
      <div className="cluster ops-leave-actions">
        <Button type="button" disabled={busy} onClick={() => void save()}>
          Save auto-leave
        </Button>
        {leave.source === "console" ? (
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void reset()}>
            Use defaults
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function toDraft(settings: OpsLeaveSettings): Record<keyof OpsLeaveSettings, string> {
  return {
    waiting_room_timeout_secs: String(settings.waiting_room_timeout_secs),
    noone_joined_timeout_secs: String(settings.noone_joined_timeout_secs),
    everyone_left_timeout_secs: String(settings.everyone_left_timeout_secs),
    silence_timeout_secs: String(settings.silence_timeout_secs),
    silence_activate_after_secs: String(settings.silence_activate_after_secs),
  };
}

export default function OpsNotetakerCard() {
  const [data, setData] = useState<OpsNotetakerOut | null>(null);
  const [busy, setBusy] = useState<BotPlatform | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<OpsNotetakerOut>("GET", "/ops/notetaker")
      .then(setData)
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load the notetaker settings."));
  }, []);

  async function change(platform: BotPlatform, req: () => Promise<OpsBotPlatformOut>) {
    setBusy(platform);
    setError(null);
    try {
      const next = await req();
      setData((prev) =>
        prev ? { ...prev, platforms: prev.platforms.map((p) => (p.platform === platform ? next : p)) } : prev,
      );
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : `Couldn't update ${PLATFORM_LABEL[platform]}.`);
    } finally {
      setBusy(null);
    }
  }

  const items = data
    ? ORDER.map((p) => data.platforms.find((i) => i.platform === p)).filter(
        (i): i is OpsBotPlatformOut => i !== undefined,
      )
    : [];

  return (
    <Card size="sm" title="Notetaker bot platforms">
      <p className="gk-sm ops-card-note">
        Which meeting platforms the notetaker bot may join, for every org. Off refuses manual joins and skips
        auto-record. Each org can also turn the bot off for itself.
        {data ? (
          <>
            {" "}
            Join mode: <span className="mono">{data.join_mode}</span>
            {data.join_mode === "stub" ? " (no real bot joins on this deployment)." : "."}
          </>
        ) : null}
      </p>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!data && !error ? <div className="gk-sm">Loading…</div> : null}
      {items.map((item) => (
        <PlatformRow
          key={item.platform}
          item={item}
          busy={busy !== null}
          onSet={(enabled) =>
            void change(item.platform, () =>
              api<OpsBotPlatformOut>("PUT", `/ops/notetaker/platforms/${item.platform}`, { enabled }),
            )
          }
          onReset={() =>
            void change(item.platform, () =>
              api<OpsBotPlatformOut>("DELETE", `/ops/notetaker/platforms/${item.platform}`),
            )
          }
        />
      ))}
      {data?.leave ? (
        <LeaveSection
          leave={data.leave}
          onSaved={(next) => {
            setError(null);
            setData((prev) => (prev ? { ...prev, leave: next } : prev));
          }}
          onError={setError}
        />
      ) : null}
    </Card>
  );
}
